import { Grid } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import React, { useEffect } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';
import styled from 'styled-components';
import { StopsIcon } from '../../../../../assets/svg/Plan/MenuBar/StopsIcon';
import { StopsCreateMenu } from '../../../../../components/menus/StopsCreateMenu';
import { Stop } from '../../../../../domain/stop/Stop';
import { useSetActivePlanFocusedStop } from '../../../../../hooks/plan/useSetActivePlanFocusedStop';
import Text from '../../../../../react-components/Text';
import { StopChip } from '../../plan-stop-chip/StopChip';
import { VirtualizedRowContainer, VirtualizedStopRow } from '../../plan-stop-chip/VirtualizedStopRow';

const UnassignedStopsListContainer = styled(Grid)`
  padding: 4px;
  flex-grow: 1;
`;

export const UnassignedStopsList = ({ stops }: { stops: Stop[] }) => {
  const listRef = React.createRef<any>();
  const { focusedStop } = useSetActivePlanFocusedStop();

  useEffect(() => {
    if (focusedStop && listRef.current) {
      const unassignedStopsId = stops.map((stop) => stop.id);
      if (unassignedStopsId.includes(focusedStop.id)) {
        const index = unassignedStopsId.indexOf(focusedStop.id);
        listRef.current.scrollToItem(index, `center`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedStop]);

  return (
    <UnassignedStopsListContainer>
      <Droppable
        droppableId={`unassigned`}
        mode="virtual"
        renderClone={(provided, snapshot, rubric) => (
          <VirtualizedRowContainer ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
            <StopChip stop={stops[rubric.source.index]} isDragging={snapshot.isDragging} />
          </VirtualizedRowContainer>
        )}
      >
        {(provided, snapshot) => (
          <>
            {stops.length === 0 && (
              <Grid
                container
                direction="column"
                alignItems="center"
                ref={provided.innerRef}
                style={{
                  padding: `24px`,
                  backgroundColor: snapshot.isDraggingOver ? RoutalPalette.secondary40 + `22` : `transparent`,
                }}
              >
                <StopsIcon style={{ width: `60px`, height: `60px` }} color={RoutalPalette.secondary60} />
                <VSpacer small />
                <Text intlId="plans.plan.data.overview.services.empty_placeholder.title" />
                <VSpacer small />
                <StopsCreateMenu
                  id="plan-overview-create-stops"
                  intlId="plans.plan.data.services.add_new"
                  style={{
                    backgroundColor: RoutalPalette.secondary40,
                    justifyContent: `space-between`,
                  }}
                  containerStyle={{
                    width: `unset`,
                  }}
                  hideSeparator
                />
              </Grid>
            )}
            {stops.length > 0 && (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{
                  height: `max(min(${64 * stops.length}px, 100%), 30vh)`,
                  backgroundColor: snapshot.isDraggingOver ? RoutalPalette.secondary40 + `22` : `transparent`,
                }}
              >
                <AutoSizer>
                  {({ height, width }: any) => {
                    return (
                      <List
                        ref={listRef}
                        height={height}
                        width={width}
                        itemCount={stops.length}
                        itemSize={64}
                        outerRef={provided.innerRef}
                        itemData={stops}
                      >
                        {VirtualizedStopRow}
                      </List>
                    );
                  }}
                </AutoSizer>
              </div>
            )}
          </>
        )}
      </Droppable>
    </UnassignedStopsListContainer>
  );
};
